<template>
    <div>
        <div class="min-h-screen flex relative lg:static surface-ground">
            <!-- app-sidebar -->
            <div
                id="app-sidebar"
                :class="[
                    {'block': sidebarShow},
                    sidebarCollapsed ? 'sidebar-collapsed' : 'sidebar-expanded'
                ]"
                class="app-sidebar surface-section h-full lg:h-auto hidden lg:block flex-shrink-0 absolute lg:static left-0 top-0 z-1 border-right-1 surface-border select-none">
                <!-- flex-column -->
                <div class="h-full bg-gray-100 flex flex-column">

                    <!-- close button (mobile only) -->
                    <div class="flex justify-content-end align-items-center">
                        <a @click="toggleSidebar" class="cursor-pointer block lg:hidden text-700 mr-3 mt-1 p-ripple" data-pd-ripple="true">
                            <i class="pi pi-times"></i>
                        </a>
                    </div>

                    <!-- logo / title -->
                    <div
                        class="sidebar-logo text-center font-semibold text-primary overflow-hidden"
                        :class="sidebarCollapsed ? 'p-1' : 'p-1 lg:p-3 text-2xl'">
                        <span v-if="!sidebarCollapsed" class="text-2xl font-bold text-primary sidebar-label">GTproject</span>
                        <span v-else class="text-xl font-bold text-primary">GT</span>
                    </div>

                    <!-- toggle collapse button (desktop only) -->
                    <div class="hidden lg:flex justify-content-center py-1">
                        <Button
                            :icon="sidebarCollapsed ? 'pi pi-angle-right' : 'pi pi-angle-left'"
                            class="sidebar-toggle-btn p-button-text p-button-rounded p-button-sm"
                            :title="sidebarCollapsed ? 'Expandir menu' : 'Recolher menu'"
                            @click="toggleCollapse"
                            size="small"
                        />
                    </div>

                    <!-- ===== EXPANDED MENU (Tree) ===== -->
                    <div v-if="!sidebarCollapsed" class="flex-1 overflow-hidden">
                        <!-- MENU SKELETON -->
                        <Tree
                            v-if="isLoading"
                            :value="skeleton"
                            :filter="false"
                            class="main-menu w-full bg-gray-100">
                            <template #nodeicon="{node}">
                                <Skeleton size="1rem"></Skeleton>
                            </template>
                            <template #default="{node}">
                                <div class="flex align-items-center gap-2">
                                    <Skeleton width="11rem"></Skeleton>
                                </div>
                            </template>
                        </Tree>

                        <!-- MENU CARREGADO -->
                        <Tree
                            v-else
                            :value="sidebar"
                            v-model:expandedKeys="expandedKeys"
                            :filter="true"
                            scrollHeight="calc(70vh)"
                            class="main-menu w-full bg-gray-100">
                            <template #default="{node}">
                                <template v-if="node.para">
                                    <RouterLink class="main-menu-item" :to="node.para">{{ node.label }}</RouterLink>
                                </template>
                                <template v-else>
                                    <span class="font-semibold">{{ node.label }}</span>
                                </template>
                            </template>
                        </Tree>
                    </div>

                    <!-- ===== COLLAPSED MENU (icon-only) ===== -->
                    <div v-else class="collapsed-menu flex-1 overflow-y-auto overflow-x-hidden py-1">
                        <template v-if="isLoading">
                            <div v-for="n in 5" :key="n" class="flex justify-content-center py-2">
                                <Skeleton size="1.8rem" shape="circle"></Skeleton>
                            </div>
                        </template>
                        <template v-else>
                            <template v-for="node in sidebar" :key="node.key">
                                <!-- leaf node with route -->
                                <template v-if="node.para && !node.children">
                                    <RouterLink
                                        :to="node.para"
                                        class="collapsed-item flex justify-content-center align-items-center"
                                        v-tooltip.right="{ value: node.label, showDelay: 200 }">
                                        <i :class="node.icon || 'pi pi-circle'" class="text-lg"></i>
                                    </RouterLink>
                                </template>

                                <!-- group node with children -->
                                <template v-else-if="node.children && node.children.length">
                                    <div
                                        class="collapsed-item collapsed-group flex justify-content-center align-items-center"
                                        v-tooltip.right="{ value: node.label, showDelay: 200 }"
                                        @mouseenter="openGroupOverlay($event, node.key)"
                                        @mouseleave="scheduleCloseOverlay(node.key)"
                                        :data-group-key="node.key">
                                        <i :class="node.icon || 'pi pi-folder'" class="text-lg"></i>
                                        <i class="pi pi-angle-right text-xs ml-1 text-500"></i>
                                    </div>

                                    <!-- inline overlay panel for this group -->
                                    <div
                                        v-if="activeGroupKey === node.key"
                                        class="group-overlay surface-card border-1 surface-border border-round shadow-3"
                                        :style="groupOverlayStyle"
                                        @mouseenter="cancelCloseOverlay(node.key)"
                                        @mouseleave="scheduleCloseOverlay(node.key)">
                                        <div class="group-overlay-title px-3 py-2 font-semibold text-sm border-bottom-1 surface-border">
                                            {{ node.label }}
                                        </div>
                                        <div class="group-overlay-body">
                                            <template v-for="child in flattenChildren(node.children)" :key="child.key">
                                                <template v-if="child.para">
                                                    <RouterLink
                                                        :to="child.para"
                                                        class="group-overlay-item flex align-items-center gap-2 px-3 py-2 no-underline"
                                                        @click="activeGroupKey = null">
                                                        <i :class="child.icon || 'pi pi-minus'" class="text-sm"></i>
                                                        <span class="text-sm">{{ child.label }}</span>
                                                    </RouterLink>
                                                </template>
                                                <template v-else>
                                                    <!-- sub-group header -->
                                                    <div class="group-overlay-subheader px-3 pt-2 pb-1 text-xs font-semibold text-500 uppercase">
                                                        {{ child.label }}
                                                    </div>
                                                </template>
                                            </template>
                                        </div>
                                    </div>
                                </template>
                            </template>
                        </template>
                    </div>

                </div>
                <!-- flex-column -->
            </div>
            <!-- app-sidebar -->

            <!-- mobile sidebar backdrop -->
            <div
                v-if="sidebarShow"
                class="mobile-sidebar-backdrop lg:hidden"
                @click="toggleSidebar">
            </div>

            <!-- app-body -->
            <div class="min-h-screen flex flex-column relative flex-auto" style="min-width: 0;">
                <!-- top-bar -->
                <div class="app-topbar flex justify-content-between align-items-center px-5 border-bottom-1 relative lg:static bg-gray-600 text-gray-100" style="height: 60px;">
                    <div class="flex text-gray-100">
                        <a @click="toggleSidebar" class="cursor-pointer block lg:hidden text-700 mr-3 mt-1 p-ripple text-gray-100" data-pd-ripple="true" data-pd-styleclass="true">
                            <i class="pi pi-bars text-4xl"></i>
                            <span role="presentation" aria-hidden="true" data-p-ink="true" data-p-ink-active="false" class="p-ink" data-pc-name="ripple" data-pc-section="root"></span>
                        </a>
                    </div>

                    <ul class="list-none hidden lg:flex my-0 align-items-center gap-2">
                        <li v-if="usuario.id">
                            <CaixaBadge />
                        </li>
                        <li v-if="usuario.id">
                            <NotificacaoBadge />
                        </li>
                        <li>
                            <Button
                                :icon="isDarkMode ? 'pi pi-sun' : 'pi pi-moon'"
                                :label="isDarkMode ? 'Light' : 'Dark'"
                                size="small"
                                class="darkmode-toggle-btn"
                                @click="toggleDarkMode"
                            />
                        </li>
                        <li v-if="usuario.id" class="border-top-1 surface-border lg:border-top-none font-semibold ">
                            <a @click="toggleMenu" class="flex p-3 lg:px-3 lg:py-2 align-items-center hover:text-900 hover:surface-100 font-small border-round cursor-pointer p-ripple" data-pd-ripple="true">
                                <span class="mr-2">{{ usuario.nome }}</span>
                                <i class="pi pi-user text-base lg:text-xl mr-2 lg:mr-0 p-overlay-badge" data-pd-badge="true" data-p-overlay-badge="true"></i>
                            </a>
                        </li>
                    </ul>

                    <div class="mobile-top-actions lg:hidden">
                        <CaixaBadge v-if="usuario.id" />
                        <NotificacaoBadge v-if="usuario.id" />
                        <Button
                            :icon="isDarkMode ? 'pi pi-sun' : 'pi pi-moon'"
                            class="mobile-theme-btn"
                            size="small"
                            @click="toggleDarkMode"
                        />
                        <a class="cursor-pointer block text-700 p-ripple text-gray-100" data-pd-ripple="true" data-pd-styleclass="true" @click="toggleMenu">
                            <i class="pi pi-ellipsis-v text-2xl"></i>
                            <span role="presentation" aria-hidden="true" data-p-ink="true" data-p-ink-active="false" class="p-ink" data-pc-name="ripple" data-pc-section="root"></span>
                        </a>
                    </div>
                    <TieredMenu ref="menu" id="overlay_tmenu" :model="items" popup />
                </div>
                <!-- top-bar -->

                <!-- content -->
                <div class="app-content flex flex-column flex-auto p-2">
                    <router-view :key="$route.path + '-theme-' + chartRefreshKey"></router-view>
                </div>
                <!-- content -->

                <div class="app-footer mt-auto p-2 text-right bg-gray-800 text-gray-400 text-xs">
                    GTproject &copy; {{ new Date().getFullYear() }} - GTS Net
                </div>
            </div>
            <!-- app-body -->
        </div>

        <ProfileForm :visible="visibleProfileForm" :data="usuario" @close="visibleProfileForm=false" @save="onSaveProfileForm" />
        <PasswordForm :visible="visiblePasswordForm" :data="usuario" @close="visiblePasswordForm=false" />

        <!-- PWA Install Prompt -->
        <div v-if="showInstallPrompt" class="fixed bottom-0 left-0 right-0 p-3 surface-card shadow-8 flex align-items-center justify-content-between z-5" style="border-top: 2px solid var(--primary-color);">
            <div class="flex align-items-center gap-2">
                <i class="pi pi-download text-primary text-xl"></i>
                <span class="text-sm">Instale o GTproject no seu dispositivo</span>
            </div>
            <div class="flex gap-1">
                <Button label="Instalar" icon="pi pi-download" size="small" severity="success" @click="installPwa" />
                <Button icon="pi pi-times" size="small" severity="secondary" text rounded @click="showInstallPrompt = false" />
            </div>
        </div>
    </div>
</template>

<script>
import Button from 'primevue/button';
import Tree from 'primevue/tree';
import TieredMenu from 'primevue/tieredmenu';
import Drawer from 'primevue/drawer';
import Skeleton from 'primevue/skeleton';

import ProfileForm from '@/components/auth/ProfileForm.vue';
import PasswordForm from '@/components/auth/PasswordForm.vue';
import NotificacaoBadge from '@/components/NotificacaoBadge.vue';
import CaixaBadge from '@/components/CaixaBadge.vue';

import Auth from '@/service/auth.js';
import Menu from '@/service/menu.js';

import Token from '@/utils/Token.js';
import AuthUser from '@/utils/AuthUser.js';

export default {
    components: {
        Button,
        Drawer,
        Tree,
        Skeleton,
        TieredMenu,
        ProfileForm,
        PasswordForm,
        NotificacaoBadge,
        CaixaBadge,
    },
    data() {
        return {
            isLoading: false,
            isDarkMode: false,
            chartRefreshKey: 0,
            visibleProfileForm: false,
            visiblePasswordForm: false,
            usuario: {},
            sidebarShow: false,
            sidebarCollapsed: false,
            expandedKeys: {},
            activeGroupKey: null,
            groupOverlayStyle: {},
            _overlayCloseTimer: null,
            showInstallPrompt: false,
            deferredPrompt: null,
            items: [
                {
                    label: 'Perfil',
                    icon: 'pi pi-user',
                    command: this.perfil
                },
                {
                    label: 'Senha',
                    icon: 'pi pi-lock',
                    command: this.senha
                },
                {
                    separator: true
                },
                {
                    label: 'Sair',
                    icon: 'pi pi-sign-out',
                    command: this.logout
                },
            ],
            skeleton: [
                {},{},{},
            ],
            sidebar: [],
        };
    },
    beforeMount() {
        this.loadThemePreference();
        this.loadCollapsePreference();

        this.me();

        this.isLoading = true;
        Menu.side()
                .then(response => {
                    this.sidebar = response.data;

                    this.expandedKeys = {};
                    this.expandAll(this.sidebar);
                })
                .catch(error => {
                    if(error.response) {
                        if(error.response.data.msg) {
                            this.$toast.add({ severity: 'error', summary: 'Ops!', detail: error.response.data.msg, life: 5000 });
                            return;
                        }
                    }
                    this.$toast.add({ severity: 'error', summary: 'Ops!', detail: error.message, life: 5000 });
                })
                .finally(() => {
                    this.isLoading = false;
                });
    },
    mounted() {
        // PWA install prompt
        window.addEventListener('beforeinstallprompt', (e) => {
            e.preventDefault();
            this.deferredPrompt = e;
            this.showInstallPrompt = true;
        });
    },
    beforeUnmount() {
        if (this._overlayCloseTimer) clearTimeout(this._overlayCloseTimer);
    },
    methods: {
        onSaveProfileForm(usuario) {
            this.usuario = usuario;
        },
        installPwa() {
            if (this.deferredPrompt) {
                this.deferredPrompt.prompt();
                this.deferredPrompt.userChoice.then(() => {
                    this.deferredPrompt = null;
                    this.showInstallPrompt = false;
                });
            }
        },
        loadThemePreference() {
            this.isDarkMode = localStorage.getItem('gts_theme_dark') === '1';
            this.applyThemeClass();
        },
        loadCollapsePreference() {
            this.sidebarCollapsed = localStorage.getItem('gts_sidebar_collapsed') === '1';
        },
        applyThemeClass() {
            document.documentElement.classList.toggle('my-app-dark', this.isDarkMode);
            window.dispatchEvent(new Event('theme-changed'));
        },
        toggleDarkMode() {
            this.isDarkMode = !this.isDarkMode;
            localStorage.setItem('gts_theme_dark', this.isDarkMode ? '1' : '0');
            this.applyThemeClass();
            this.chartRefreshKey += 1;
        },
        toggleCollapse() {
            this.sidebarCollapsed = !this.sidebarCollapsed;
            localStorage.setItem('gts_sidebar_collapsed', this.sidebarCollapsed ? '1' : '0');
            // close any open overlay when toggling
            this.activeGroupKey = null;
        },
        me() {
            Auth.me()
                .then(response => {
                        this.usuario = response.data;
                        AuthUser.set(response.data);
                    })
                    .catch(error => {
                        if(error.response) {
                            if(error.response.data.msg) {
                                this.$toast.add({ severity: 'error', summary: 'Ops!', detail: error.response.data.msg, life: 5000 });
                                return;
                            }
                        }
                        this.$toast.add({ severity: 'error', summary: 'Ops!', detail: error.message, life: 5000 });
                    });
        },
        toggleSidebar() {
            this.sidebarShow = !this.sidebarShow;
        },
        toggleMenu(event) {
            this.$refs.menu.toggle(event);
        },
        expandAll(lista) {
            lista.forEach(item => {
                this.expandedKeys[item.key] = true;
                if(item.children) {
                    this.expandAll(item.children);
                }
            });
        },
        // Flatten children tree into a flat list for overlay rendering.
        // Sub-groups appear as non-link headers.
        flattenChildren(children, depth) {
            depth = depth || 0;
            const result = [];
            children.forEach(child => {
                result.push(child);
                if (child.children && child.children.length) {
                    this.flattenChildren(child.children, depth + 1).forEach(c => result.push(c));
                }
            });
            return result;
        },
        openGroupOverlay(event, key) {
            // Cancel any pending close for this key
            this.cancelCloseOverlay(key);

            const triggerEl = event.currentTarget;
            const rect = triggerEl.getBoundingClientRect();
            const sidebarEl = document.getElementById('app-sidebar');
            const sidebarRect = sidebarEl ? sidebarEl.getBoundingClientRect() : { right: 60 };

            this.groupOverlayStyle = {
                position: 'fixed',
                top: rect.top + 'px',
                left: sidebarRect.right + 4 + 'px',
                zIndex: 1300,
                minWidth: '180px',
                maxWidth: '260px',
            };
            this.activeGroupKey = key;
        },
        scheduleCloseOverlay(key) {
            // Only close if this is the currently active key
            if (this.activeGroupKey !== key) return;
            this._overlayCloseTimer = setTimeout(() => {
                if (this.activeGroupKey === key) {
                    this.activeGroupKey = null;
                }
            }, 120);
        },
        cancelCloseOverlay(key) {
            if (this._overlayCloseTimer) {
                clearTimeout(this._overlayCloseTimer);
                this._overlayCloseTimer = null;
            }
        },
        perfil() {
            this.visibleProfileForm=true;
        },
        senha() {
            this.visiblePasswordForm=true;
        },
        logout() {
            this.$confirm.require({
                message: 'Deseja realmente sair do sistema?',
                header: 'Sair do Sistema',
                icon: 'pi pi-sign-out',
                acceptLabel: 'Sair',
                acceptClass: 'bg-green-300 text-white',
                rejectLabel: 'Cancelar',
                rejectClass: 'bg-red-400 text-white',
                accept: () => {
                    Auth.logout()
                        .then(() => {
                            Token.remove();
                            AuthUser.clear();
                            this.$toast.add({ severity: 'warn', summary: 'Até logo!', detail: `Você está saindo do sistema.`, life: 3000 });
                            this.$router.push('/login');
                        })
                        .catch(error => {
                            if(error.response) {
                                if(error.response.data.msg) {
                                    this.$toast.add({ severity: 'error', summary: 'Ops!', detail: error.response.data.msg, life: 5000 });
                                    return;
                                }
                            }
                            this.$toast.add({ severity: 'error', summary: 'Ops!', detail: error.message, life: 5000 });
                        });
                },
            });
        }
    }
};
</script>

<style>
    /* =====================================================
       SIDEBAR WIDTH TRANSITIONS
       ===================================================== */
    .app-sidebar {
        transition: width 0.3s ease;
    }

    .app-sidebar.sidebar-expanded {
        width: 280px;
    }

    .app-sidebar.sidebar-collapsed {
        width: 60px;
    }

    /* labels fade out when collapsing */
    .sidebar-label {
        transition: opacity 0.2s ease;
    }
    .sidebar-collapsed .sidebar-label {
        opacity: 0;
        pointer-events: none;
    }

    /* =====================================================
       TOGGLE BUTTON
       ===================================================== */
    .sidebar-toggle-btn.p-button {
        background: transparent !important;
        border: 1px solid var(--surface-border) !important;
        color: var(--p-text-color, #374151) !important;
        width: 1.75rem !important;
        height: 1.75rem !important;
        padding: 0 !important;
    }
    .sidebar-toggle-btn.p-button:hover {
        background: var(--surface-hover) !important;
    }
    .my-app-dark .sidebar-toggle-btn.p-button {
        color: #e5e7eb !important;
        border-color: #374151 !important;
    }

    /* =====================================================
       EXPANDED TREE MENU (unchanged styles)
       ===================================================== */
    .main-menu .p-tree-root * {
        font-size: 0.9rem;
    }

    .main-menu a {
        text-decoration: none;
        color: var(--p-tree-node-color);
    }
    .router-link-active {
        font-weight: bold;
    }

    .main-menu .p-icon {
        width: 0.8rem !important;
        height: 0.8rem !important;
    }

    .main-menu .p-tree-node-toggle-button {
        width: 0.5rem;
        height: 0.5rem;
    }

    .main-menu .p-tree-node-content {
        padding-top: 0.9;
    }

    /* =====================================================
       COLLAPSED MENU — icon-only items
       ===================================================== */
    .collapsed-menu {
        padding-top: 0.25rem;
    }

    .collapsed-item {
        position: relative;
        width: 100%;
        height: 40px;
        cursor: pointer;
        border-radius: 6px;
        margin: 2px 4px;
        width: calc(100% - 8px);
        color: var(--p-tree-node-color, #374151);
        text-decoration: none;
        transition: background 0.15s ease;
    }

    .collapsed-item:hover {
        background: var(--surface-hover, #f6f9fc);
    }

    .collapsed-item i {
        color: var(--p-text-color, #374151);
    }

    .router-link-active.collapsed-item {
        background: var(--p-primary-50, #eff6ff);
        font-weight: bold;
    }
    .router-link-active.collapsed-item i {
        color: var(--primary-color, #2563EB);
    }

    /* dark mode for collapsed items */
    .my-app-dark .collapsed-item {
        color: #e5e7eb;
    }
    .my-app-dark .collapsed-item i {
        color: #e5e7eb;
    }
    .my-app-dark .collapsed-item:hover {
        background: rgba(255, 255, 255, 0.08);
    }
    .my-app-dark .router-link-active.collapsed-item {
        background: rgba(37, 99, 235, 0.25);
    }

    /* =====================================================
       GROUP OVERLAY POPUP
       ===================================================== */
    .group-overlay {
        border-radius: 6px;
        overflow: hidden;
        animation: overlayFadeIn 0.15s ease;
    }

    @keyframes overlayFadeIn {
        from { opacity: 0; transform: translateX(-6px); }
        to   { opacity: 1; transform: translateX(0); }
    }

    .group-overlay-title {
        background: var(--surface-50, #fafafa);
        color: var(--p-text-color, #374151);
    }

    .group-overlay-item {
        color: var(--p-text-color, #374151);
        text-decoration: none;
        transition: background 0.12s ease;
    }
    .group-overlay-item:hover {
        background: var(--surface-hover, #f6f9fc);
    }
    .group-overlay-item.router-link-active {
        font-weight: bold;
        color: var(--primary-color, #2563EB);
    }

    .group-overlay-subheader {
        color: var(--p-text-muted-color, #9e9e9e);
        letter-spacing: 0.04em;
    }

    /* dark mode overlays */
    .my-app-dark .group-overlay {
        background: #1f2937;
        border-color: #374151;
    }
    .my-app-dark .group-overlay-title {
        background: #111827;
        color: #e5e7eb;
        border-color: #374151;
    }
    .my-app-dark .group-overlay-item {
        color: #e5e7eb;
    }
    .my-app-dark .group-overlay-item:hover {
        background: rgba(255, 255, 255, 0.08);
    }
    .my-app-dark .group-overlay-item.router-link-active {
        color: #60a5fa;
    }
    .my-app-dark .group-overlay-subheader {
        color: #6b7280;
    }

    /* =====================================================
       RESPONSIVE (mobile) — sidebar is always full-width overlay
       ===================================================== */
    @media (max-width: 1023px) {
        #app-sidebar.app-sidebar {
            position: fixed;
            width: min(86vw, 320px) !important;
            height: 100dvh !important;
            overflow-y: auto;
            z-index: 1202 !important;
            box-shadow: 0 0 0 9999px transparent;
        }

        .mobile-sidebar-backdrop {
            position: fixed;
            inset: 0;
            z-index: 1201;
            background: rgba(15, 23, 42, 0.45);
        }

        /* ensure main content stays below mobile overlay */
        .min-h-screen.flex.flex-column.relative.flex-auto {
            z-index: 1;
            min-width: 0;
        }

        .app-topbar {
            padding-left: 0.75rem !important;
            padding-right: 0.75rem !important;
        }

        .app-topbar .pi-bars {
            font-size: 1.75rem !important;
        }

        .app-content {
            padding: 0.5rem !important;
        }

        .app-footer {
            text-align: center;
            line-height: 1.35;
        }

        .app-logo {
            width: 140px;
            max-width: 100%;
            height: auto;
        }

        .mobile-top-actions {
            display: flex;
            align-items: center;
            gap: 0.25rem;
        }

        .mobile-theme-btn.p-button {
            background: transparent !important;
            border: 1px solid rgba(255, 255, 255, 0.75) !important;
            color: #ffffff !important;
            padding: 0.3rem 0.45rem !important;
        }

        .mobile-theme-btn .p-button-icon {
            color: #ffffff !important;
        }
    }

    /* =====================================================
       DARK MODE — top bar / darkmode button
       ===================================================== */
    .darkmode-toggle-btn.p-button {
        background: transparent !important;
        border: 1px solid rgba(255, 255, 255, 0.8) !important;
        color: #ffffff !important;
    }

    .darkmode-toggle-btn.p-button:hover {
        background: rgba(255, 255, 255, 0.2) !important;
        border-color: #ffffff !important;
    }

    .darkmode-toggle-btn .p-button-icon,
    .darkmode-toggle-btn .p-button-label {
        color: #ffffff !important;
    }
</style>
